import { Anchor, Code, Group, Select, Stack, Text } from "@mantine/core";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { DataTable, FilterBar, PageHeader, RecordDrawer, useSearchParamState } from "@how/ui";
import { isAuthorizationError } from "../../lib/query-client";
import {
  NO_CHURCH,
  SURFACE_LABEL,
  isErrorSurface,
  normalizeErrorReference,
  useChurchOptions,
  useErrorEvents,
  type ErrorEventRow,
} from "./error-queries";

const PAGE_SIZE = 50;
const NO_SORTING: never[] = [];

const columns: ColumnDef<ErrorEventRow>[] = [
  {
    accessorKey: "received_at",
    header: "Received",
    enableSorting: false,
    meta: { numeric: true },
    cell: (ctx) => new Date(ctx.row.original.received_at).toLocaleString(),
  },
  {
    accessorKey: "reference",
    header: "Reference",
    enableSorting: false,
    cell: (ctx) => <span className="how-numeric">{ctx.row.original.reference}</span>,
  },
  {
    id: "church",
    header: "Church",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.tenant === null ? (
        <Text size="sm" c="dimmed">
          No church
        </Text>
      ) : (
        ctx.row.original.tenant.name
      ),
  },
  {
    accessorKey: "surface",
    header: "Surface",
    enableSorting: false,
    cell: (ctx) => {
      const s = ctx.row.original.surface;
      return isErrorSurface(s) ? SURFACE_LABEL[s] : s;
    },
  },
  { accessorKey: "route", header: "Where", enableSorting: false },
  {
    accessorKey: "message",
    header: "Error",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" lineClamp={1} maw={420}>
        <Text span size="sm" fw={600}>
          {ctx.row.original.error_class}
        </Text>
        {ctx.row.original.message === "" ? null : `: ${ctx.row.original.message}`}
      </Text>
    ),
  },
];

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text c="dimmed" size="sm" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" ta="right" style={{ overflowWrap: "anywhere" }}>
        {children}
      </Text>
    </Group>
  );
}

/** One error event: what support reads back to a church, and what an engineer needs to find the cause. */
function ErrorEventDrawer({
  event,
  onClose,
}: {
  event: ErrorEventRow | null;
  onClose: () => void;
}) {
  return (
    <RecordDrawer
      opened={event !== null}
      onClose={onClose}
      title={event?.reference ?? "Error"}
      subtitle={
        event === null ? undefined : (
          <Text size="sm" c="dimmed" mb="md">
            {new Date(event.received_at).toLocaleString()}
          </Text>
        )
      }
    >
      {event === null ? null : (
        <Stack gap="lg">
          <Stack gap="xs">
            <Field label="Church">
              {event.tenant === null || event.tenant_id === null ? (
                "No church"
              ) : (
                // Primary text with a standing underline: brand blue measures 4.44:1 on the dark drawer.
                <Anchor
                  component={Link}
                  to={`/tenants/${event.tenant_id}`}
                  size="sm"
                  c="var(--how-text-primary)"
                  underline="always"
                >
                  {event.tenant.name}
                </Anchor>
              )}
            </Field>
            <Field label="Surface">
              {isErrorSurface(event.surface) ? SURFACE_LABEL[event.surface] : event.surface}
            </Field>
            <Field label="Where">{event.route}</Field>
            <Field label="Happened">{new Date(event.occurred_at).toLocaleString()}</Field>
            <Field label="Release">{event.release}</Field>
            <Field label="Signed-in user">{event.user_id ?? "Nobody signed in"}</Field>
            <Field label="Correlation id">{event.correlation_id ?? "None"}</Field>
          </Stack>
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              {event.error_class}
            </Text>
            <Text size="sm" style={{ overflowWrap: "anywhere" }}>
              {event.message === "" ? "No message." : event.message}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Stack
            </Text>
            {event.stack === null ? (
              <Text size="sm" c="dimmed">
                No stack was reported.
              </Text>
            ) : (
              <Code block style={{ whiteSpace: "pre", overflowX: "auto" }}>
                {event.stack}
              </Code>
            )}
          </Stack>
          <Text size="xs" c="dimmed">
            Scrubbed before it was stored: no email addresses, telephone numbers, credentials or
            query strings.
          </Text>
        </Stack>
      )}
    </RecordDrawer>
  );
}

/**
 * Recent errors across the platform (ADR 0038, #114), newest first. Support types the reference a person read to
 * them and gets the one row; otherwise the list filters by church and surface. Readable by platform staff only,
 * which the table's policy enforces whatever this screen shows.
 */
export function ErrorsPage() {
  const [search] = useSearchParamState("ref");
  const [church] = useSearchParamState("church");
  const [surfaceParam] = useSearchParamState("surface");
  const [page, setPage] = useSearchParamState("page", "1");
  const [selectedId, setSelectedId] = useSearchParamState("event");
  const [, setParams] = useSearchParams();
  /**
   * A filter change and the return to the first page in one navigation. Two useSearchParamState setters in one event
   * each start from the parameters of the last render, so the second would undo the first.
   */
  const setFilter = (key: "ref" | "church" | "surface", value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };
  const churches = useChurchOptions();

  const typed = search.trim();
  const reference = typed === "" ? null : normalizeErrorReference(typed);
  const notAReference = typed !== "" && reference === null;
  const surface = isErrorSurface(surfaceParam) ? surfaceParam : null;
  const pageIndex = Math.max(0, Number.parseInt(page, 10) - 1 || 0);
  const events = useErrorEvents(
    {
      reference,
      church: church === "" ? null : church,
      surface,
      pageIndex: reference === null ? pageIndex : 0,
      pageSize: PAGE_SIZE,
    },
    { enabled: !notAReference },
  );
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: PAGE_SIZE }),
    [pageIndex],
  );
  const rows = notAReference ? [] : (events.data?.rows ?? []);
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const status = notAReference
    ? "ready"
    : events.isPending
      ? "loading"
      : events.isError
        ? isAuthorizationError(events.error)
          ? "denied"
          : "error"
        : "ready";

  const churchOptions = [
    { value: NO_CHURCH, label: "No church" },
    ...(churches.data ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.slug})` })),
  ];

  return (
    <>
      <PageHeader
        title="Errors"
        description="What failed, where, and for which church. Type the reference a person read to you, such as E-7K3Q-9MZD, to find their error."
      />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setFilter("ref", value);
        }}
        searchPlaceholder="Find a reference"
        filters={
          <>
            <Select
              aria-label="Filter by church"
              placeholder="Any church"
              searchable
              clearable
              disabled={reference !== null}
              data={churchOptions}
              value={church === "" ? null : church}
              onChange={(value) => {
                setFilter("church", value);
              }}
              w={{ base: "100%", sm: 240 }}
            />
            <Select
              aria-label="Filter by surface"
              placeholder="Any surface"
              clearable
              disabled={reference !== null}
              data={Object.entries(SURFACE_LABEL).map(([value, label]) => ({ value, label }))}
              value={surface}
              onChange={(value) => {
                setFilter("surface", value);
              }}
              w={{ base: "100%", sm: 200 }}
            />
          </>
        }
      />
      <DataTable
        label="Errors"
        density="dense"
        minWidth={960}
        columns={columns}
        rows={rows}
        totalRows={notAReference ? 0 : (events.data?.total ?? 0)}
        status={status}
        pagination={pagination}
        onPaginationChange={(updater) => {
          const next = typeof updater === "function" ? updater(pagination) : updater;
          setPage(String(next.pageIndex + 1));
        }}
        sorting={NO_SORTING}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setSelectedId(row.id);
        }}
        emptyTitle={
          notAReference
            ? "That is not a reference"
            : reference !== null
              ? `No error with the reference ${reference}`
              : "No errors"
        }
        emptyDescription={
          notAReference
            ? "A reference is E- and eight letters and numbers, such as E-7K3Q-9MZD."
            : reference !== null
              ? "Errors are kept 30 days. Check the reference with the person, or ask for the church, the time and what they were doing."
              : church !== "" || surface !== null
                ? "Nothing failed here in the last 30 days. Try another church or surface."
                : "Nothing has failed in the last 30 days."
        }
        errorMessage="Could not load errors."
        onRetry={() => {
          void events.refetch();
        }}
      />
      <ErrorEventDrawer
        event={selected}
        onClose={() => {
          setSelectedId(undefined);
        }}
      />
    </>
  );
}
